/**
 * useOrbitViews Hook
 *
 * Centralized hook for managing OrbitViews with React Query.
 * This eliminates redundant API calls across components (Sidebar, SidebarFooter,
 * OrbitViewSelectorModal, BasicInfoTab) by sharing a single cache.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsService } from '@/services/api/projectsService';
import type { OrbitView } from '@/types/orbitView';
import { useAuth } from '@/shared/contexts/AuthContext';

// Query key factory for consistent cache management
export const orbitViewKeys = {
  all: ['orbit-views'] as const,
  lists: () => [...orbitViewKeys.all, 'list'] as const,
  list: (params?: Record<string, any>) => [...orbitViewKeys.lists(), params] as const,
  byProject: (projectId: string | undefined) => [...orbitViewKeys.all, 'project', projectId] as const,
  bySlug: (slug: string | undefined) => [...orbitViewKeys.all, 'slug', slug] as const,
  detail: (id: string) => [...orbitViewKeys.all, 'detail', id] as const,
};

/**
 * Hook to get all OrbitViews with optional filtering
 *
 * @param params - Optional filter parameters (project, company, orbit_type, etc.)
 * @param options - Additional React Query options
 */
export function useOrbitViews(
  params?: Record<string, any>,
  options?: { enabled?: boolean; staleTime?: number }
) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: orbitViewKeys.list(params),
    queryFn: async () => {
      const response = await projectsService.getOrbitViews(params);
      // Handle both paginated and non-paginated responses
      return response.results || response;
    },
    enabled: options?.enabled !== false && isAuthenticated,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes default
    gcTime: 10 * 60 * 1000, // 10 minutes in cache
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get OrbitViews for a specific project
 * Optimized for sidebar and project-specific views
 *
 * @param projectId - Project UUID to filter by
 * @param projectSlug - Optional project slug for additional filtering
 */
export function useProjectOrbitViews(
  projectId: string | undefined,
  projectSlug?: string
) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: orbitViewKeys.byProject(projectId),
    queryFn: async () => {
      if (!projectId) return [];

      const response = await projectsService.getOrbitViews({ project: projectId });
      const orbitViews = response.results || response;

      // Additional client-side filtering by slug if needed
      if (projectSlug && Array.isArray(orbitViews)) {
        return orbitViews.filter((ov: OrbitView) =>
          ov.project === projectId ||
          (ov as any).project_data?.slug === projectSlug
        );
      }

      return orbitViews;
    },
    enabled: isAuthenticated && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get a single OrbitView by ID
 */
export function useOrbitView(id: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: orbitViewKeys.detail(id!),
    queryFn: () => projectsService.getOrbitViewById(id!),
    enabled: isAuthenticated && !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get OrbitView by slug (for public pages)
 */
export function useOrbitViewBySlug(slug: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: orbitViewKeys.bySlug(slug),
    queryFn: async () => {
      if (!slug) return null;
      // This would need a specific endpoint - for now use list and filter
      const response = await projectsService.getOrbitViews({ slug });
      const orbitViews = response.results || response;
      return Array.isArray(orbitViews) ? orbitViews[0] || null : null;
    },
    enabled: isAuthenticated && !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new OrbitView
 */
export function useCreateOrbitView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<OrbitView>) => projectsService.createOrbitView(data),
    onSuccess: (newOrbitView) => {
      // Invalidate all orbit view queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: orbitViewKeys.all });

      // Optionally add the new item to the cache immediately
      queryClient.setQueryData(
        orbitViewKeys.detail(newOrbitView.id),
        newOrbitView
      );
    },
  });
}

/**
 * Hook to update an OrbitView
 */
export function useUpdateOrbitView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<OrbitView> }) =>
      projectsService.updateOrbitView(id, data),
    onSuccess: (updatedOrbitView) => {
      // Update the specific item in cache
      queryClient.setQueryData(
        orbitViewKeys.detail(updatedOrbitView.id),
        updatedOrbitView
      );

      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: orbitViewKeys.lists() });
    },
  });
}

/**
 * Hook to delete an OrbitView
 */
export function useDeleteOrbitView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => projectsService.deleteOrbitView(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: orbitViewKeys.detail(deletedId) });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: orbitViewKeys.lists() });
    },
  });
}

/**
 * Helper hook to invalidate all OrbitView caches
 * Useful when project context changes
 */
export function useInvalidateOrbitViews() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: orbitViewKeys.all });
  };
}

/**
 * Helper hook to prefetch OrbitViews for a project
 * Useful for preloading on hover
 */
export function usePrefetchProjectOrbitViews() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  return (projectId: string) => {
    if (!isAuthenticated || !projectId) return;

    queryClient.prefetchQuery({
      queryKey: orbitViewKeys.byProject(projectId),
      queryFn: async () => {
        const response = await projectsService.getOrbitViews({ project: projectId });
        return response.results || response;
      },
      staleTime: 5 * 60 * 1000,
    });
  };
}
